import { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { ChevronLeft, ChevronRight, Copy, Trash2, Move, Type } from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.4.168/legacy/build/pdf.worker.min.mjs';

const SIG_RATIO = 0.45; // hauteur signature = largeur * 0.45 (identique au backend)
let SEQ = 0;

// Rendu de la page PDF + signatures et textes déplaçables / duplicables (tactile).
// onChange renvoie { signatures: [{x,y,page,width_frac}], texts: [{text,x,y,page,size}] }
export default function ClientSignPlacer({ pdfUrl, signatureUrl, onChange }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const pdfRef = useRef(null);
  const drag = useRef(null);
  const [numPages, setNumPages] = useState(1);
  const [pageIndex, setPageIndex] = useState(0);
  const [docLoaded, setDocLoaded] = useState(false);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [ptScale, setPtScale] = useState(1); // px par point PDF
  const [sigs, setSigs] = useState([]);
  const [texts, setTexts] = useState([]);
  const [sel, setSel] = useState(null); // { type:'sig'|'text', id }
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);

  // Chargement du document
  useEffect(() => {
    let cancelled = false; let task = null;
    setReady(false); setError(false); setDocLoaded(false);
    (async () => {
      try {
        task = pdfjsLib.getDocument({ url: pdfUrl });
        const doc = await task.promise;
        if (cancelled) return;
        pdfRef.current = doc;
        const lastPage = doc.numPages - 1;
        setNumPages(doc.numPages);
        setPageIndex(lastPage);
        const id = ++SEQ;
        setSigs([{ id, x: 0.3, y: 0.72, wf: 0.4, page: lastPage }]);
        setSel({ type: 'sig', id });
        setDocLoaded(true);
      } catch (e) { if (!cancelled) setError(true); }
    })();
    return () => { cancelled = true; try { task && task.destroy(); } catch (e) {} };
  }, [pdfUrl]);

  // Rendu de la page courante
  useEffect(() => {
    if (!docLoaded) return;
    let cancelled = false;
    (async () => {
      const doc = pdfRef.current; if (!doc) return;
      try {
        const page = await doc.getPage(pageIndex + 1);
        if (cancelled) return;
        const targetW = Math.min(wrapRef.current?.clientWidth || 420, 420);
        const base = page.getViewport({ scale: 1 });
        const sc = targetW / base.width;
        const viewport = page.getViewport({ scale: sc });
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = viewport.width; canvas.height = viewport.height;
        setDims({ w: viewport.width, h: viewport.height });
        setPtScale(sc);
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (!cancelled) setReady(true);
      } catch (e) { if (!cancelled) setError(true); }
    })();
    return () => { cancelled = true; };
  }, [pageIndex, docLoaded]);

  // Remonte l'état au parent
  useEffect(() => {
    if (!onChange) return;
    onChange({
      signatures: sigs.map(s => ({ x: s.x, y: s.y, page: s.page, width_frac: s.wf })),
      texts: texts.filter(t => (t.text || '').trim()).map(t => ({ text: t.text, x: t.x, y: t.y, page: t.page, size: t.size })),
    });
  }, [sigs, texts]); // eslint-disable-line

  const startDrag = (type, id) => (e) => {
    e.preventDefault(); e.stopPropagation();
    setSel({ type, id });
    const rect = canvasRef.current.getBoundingClientRect();
    const item = (type === 'sig' ? sigs : texts).find(s => s.id === id);
    drag.current = { type, id, startX: e.clientX, startY: e.clientY, origX: item.x, origY: item.y, scaleW: rect.width, scaleH: rect.height };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {}
  };
  const onMove = useCallback((e) => {
    if (!drag.current) return;
    const d = drag.current;
    let nx = d.origX + (e.clientX - d.startX) / d.scaleW;
    let ny = d.origY + (e.clientY - d.startY) / d.scaleH;
    const clamp = (v) => Math.max(0, Math.min(v, 0.98));
    if (d.type === 'sig') {
      setSigs(list => list.map(s => s.id === d.id ? { ...s, x: Math.max(0, Math.min(nx, 1 - s.wf)), y: Math.max(0, Math.min(ny, 1 - s.wf * SIG_RATIO)) } : s));
    } else {
      setTexts(list => list.map(s => s.id === d.id ? { ...s, x: clamp(nx), y: clamp(ny) } : s));
    }
  }, []);
  const endDrag = (e) => { if (e) e.stopPropagation(); drag.current = null; };

  const duplicateSig = () => {
    const src = (sel?.type === 'sig' && sigs.find(s => s.id === sel.id)) || sigs[0];
    const id = ++SEQ;
    setSigs(list => [...list, { id, x: Math.min((src?.x ?? 0.3) + 0.08, 0.9), y: Math.min((src?.y ?? 0.7) + 0.08, 0.9), wf: src?.wf ?? 0.4, page: pageIndex }]);
    setSel({ type: 'sig', id });
  };
  const addText = () => {
    const id = ++SEQ;
    setTexts(list => [...list, { id, text: '', x: 0.2, y: 0.4, page: pageIndex, size: 14 }]);
    setSel({ type: 'text', id });
  };
  const removeSel = () => {
    if (!sel) return;
    if (sel.type === 'sig') { setSigs(list => { const n = list.filter(s => s.id !== sel.id); return n.length ? n : list; }); }
    else { setTexts(list => list.filter(s => s.id !== sel.id)); }
    setSel(null);
  };
  const placeHere = () => {
    if (!sel) return;
    if (sel.type === 'sig') setSigs(list => list.map(s => s.id === sel.id ? { ...s, page: pageIndex } : s));
    else setTexts(list => list.map(s => s.id === sel.id ? { ...s, page: pageIndex } : s));
  };
  const setSelWf = (wf) => setSigs(list => list.map(s => s.id === sel.id ? { ...s, wf, x: Math.min(s.x, 1 - wf), y: Math.min(s.y, 1 - wf * SIG_RATIO) } : s));
  const setSelSize = (size) => setTexts(list => list.map(s => s.id === sel.id ? { ...s, size } : s));
  const setSelText = (val) => setTexts(list => list.map(s => s.id === sel.id ? { ...s, text: val } : s));

  const selSig = sel?.type === 'sig' ? sigs.find(s => s.id === sel.id) : null;
  const selText = sel?.type === 'text' ? texts.find(s => s.id === sel.id) : null;
  const sigsOnPage = sigs.filter(s => s.page === pageIndex);
  const textsOnPage = texts.filter(s => s.page === pageIndex);
  const selOnOtherPage = (selSig && selSig.page !== pageIndex) || (selText && selText.page !== pageIndex);

  return (
    <div className="space-y-2" data-testid="client-sign-placer">
      <div className="flex items-center justify-between text-xs text-[#6B7280]">
        <span className="flex items-center gap-1"><Move className="h-3.5 w-3.5" /> Glissez les éléments où vous voulez</span>
        {numPages > 1 && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPageIndex(p => Math.max(0, p - 1))} disabled={pageIndex === 0} className="p-1 rounded border border-[#8B7355]/40 disabled:opacity-40" data-testid="client-pdf-prev"><ChevronLeft className="h-4 w-4" /></button>
            <span>Page {pageIndex + 1}/{numPages}</span>
            <button type="button" onClick={() => setPageIndex(p => Math.min(numPages - 1, p + 1))} disabled={pageIndex === numPages - 1} className="p-1 rounded border border-[#8B7355]/40 disabled:opacity-40" data-testid="client-pdf-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={duplicateSig} className="inline-flex items-center gap-1 border border-[#8B7355] text-[#8B7355] px-2.5 py-1 rounded text-xs font-semibold hover:bg-[#8B7355]/10" data-testid="client-duplicate-sig">
          <Copy className="h-3.5 w-3.5" /> Dupliquer signature
        </button>
        <button type="button" onClick={addText} className="inline-flex items-center gap-1 border border-[#8B7355] text-[#8B7355] px-2.5 py-1 rounded text-xs font-semibold hover:bg-[#8B7355]/10" data-testid="client-add-text">
          <Type className="h-3.5 w-3.5" /> Ajouter un texte
        </button>
        {sel && (sel.type === 'text' || sigs.length > 1) && (
          <button type="button" onClick={removeSel} className="inline-flex items-center gap-1 border border-red-400 text-red-600 px-2.5 py-1 rounded text-xs hover:bg-red-50" data-testid="client-remove-item">
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </button>
        )}
        {selOnOtherPage && (
          <button type="button" onClick={placeHere} className="inline-flex items-center gap-1 border border-emerald-500 text-emerald-700 px-2.5 py-1 rounded text-xs hover:bg-emerald-50" data-testid="client-item-here">
            Poser ici
          </button>
        )}
        <span className="text-[11px] text-[#6B7280] self-center">{sigs.length} sig · {texts.length} texte(s)</span>
      </div>

      <div ref={wrapRef} className="relative mx-auto border border-[#E5E0D8] rounded-lg overflow-hidden bg-gray-100" style={{ width: dims.w || '100%' }}>
        {error && <div className="p-6 text-center text-sm text-red-500">Impossible d'afficher l'aperçu du PDF.</div>}
        <canvas ref={canvasRef} className="block w-full" />
        {ready && !error && dims.w > 0 && sigsOnPage.map(s => (
          <div key={s.id} role="button" data-testid={`client-sig-box-${s.id}`}
            onPointerDown={startDrag('sig', s.id)} onPointerMove={onMove} onPointerUp={endDrag} onPointerCancel={endDrag}
            className={`absolute touch-none cursor-move flex items-center justify-center rounded border-2 ${sel?.type === 'sig' && sel.id === s.id ? 'border-emerald-500 bg-emerald-500/10' : 'border-dashed border-emerald-400 bg-emerald-400/5'}`}
            style={{ left: s.x * dims.w, top: s.y * dims.h, width: s.wf * dims.w, height: s.wf * dims.w * SIG_RATIO }}>
            {signatureUrl
              ? <img src={signatureUrl} alt="signature" className="w-full h-full object-contain pointer-events-none" draggable={false} />
              : <span className="text-[10px] font-semibold text-emerald-700 pointer-events-none">Signature</span>}
          </div>
        ))}
        {ready && !error && dims.w > 0 && textsOnPage.map(t => (
          <div key={t.id} role="button" data-testid={`client-text-box-${t.id}`}
            onPointerDown={startDrag('text', t.id)} onPointerMove={onMove} onPointerUp={endDrag} onPointerCancel={endDrag}
            className={`absolute touch-none cursor-move rounded px-1 border ${sel?.type === 'text' && sel.id === t.id ? 'border-blue-500 bg-blue-500/10' : 'border-dashed border-blue-400 bg-blue-400/5'}`}
            style={{ left: t.x * dims.w, top: t.y * dims.h, maxWidth: (1 - t.x) * dims.w }}>
            <span className="font-bold text-[#0F0F0F] whitespace-pre pointer-events-none leading-tight" style={{ fontSize: Math.max(8, t.size * ptScale) }}>
              {t.text || 'Texte…'}
            </span>
          </div>
        ))}
      </div>

      {selSig && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6B7280] whitespace-nowrap">Taille signature</span>
          <input type="range" min="0.15" max="0.7" step="0.01" value={selSig.wf}
            onChange={(e) => setSelWf(parseFloat(e.target.value))} className="flex-1 accent-emerald-600" data-testid="client-sig-size" />
        </div>
      )}
      {selText && (
        <div className="space-y-2 bg-blue-50 border border-blue-200 rounded-lg p-2" data-testid="client-text-editor">
          <input type="text" value={selText.text} onChange={(e) => setSelText(e.target.value)}
            placeholder="Saisissez le texte (ex : Franchise 350 €)" autoFocus
            className="w-full text-sm border border-blue-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" data-testid="client-text-input" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6B7280] whitespace-nowrap">Taille texte</span>
            <input type="range" min="8" max="36" step="1" value={selText.size}
              onChange={(e) => setSelSize(parseInt(e.target.value, 10))} className="flex-1 accent-blue-600" data-testid="client-text-size" />
          </div>
        </div>
      )}
    </div>
  );
}
